import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';

/**
 * RTK Query api for the Infynity account-creation agent (backend proxy at
 * `/v1/infynity-agent/*`, which forwards to the Odin Infynity Account API).
 *
 * The flow is two-step by design: `getCreateInformation` maps an assessment
 * into a reviewable payload without touching Infynity; the caller reviews it and
 * confirms, which queues a background job that callers poll with `getJob`.
 *
 * A sync is TWO independent operations over that one payload, each with its own
 * job downstream:
 *
 * - `updateAccountFromAssessment` — CLIENT DETAILS (the applicant form:
 *   identity, contact, addresses, employment). Creates the client if it doesn't
 *   exist yet.
 * - `updateFinancialsFromAssessment` — FINANCIALS (assets, then liabilities).
 *   Never creates a client, so run client details first for a new one.
 *
 * They are separate so client details can be corrected without re-running
 * financials, and so a financials failure can't leave the detail form
 * half-written.
 *
 * Field names are snake_case to match the downstream service exactly — the
 * whole payload is round-tripped (GET → edit → POST) verbatim.
 */

// ── Enum option values (must equal the downstream `<option value>` tokens) ──
export const TITLE_OPTIONS = [
  'Mr.',
  'Ms.',
  'Miss.',
  'Mrs.',
  'Dr.',
  'Prof.',
  'Rev.',
  'Other',
] as const;
export const GENDER_OPTIONS = ['Female', 'Male'] as const;
export const MARITAL_STATUS_OPTIONS = [
  'Single',
  'Married',
  'DeFacto',
  'Separated',
  'Divorced',
  'Widowed',
  'Other',
] as const;
export const YES_NO_OPTIONS = ['yes', 'no'] as const;
export const YES_NO_TITLECASE_OPTIONS = ['Yes', 'No'] as const;
export const HOUSING_SITUATION_OPTIONS = [
  'Renting',
  'Boarding',
  'Own Home',
  'Own Home Mortgage',
  'Living With Parents',
  'Caravan',
  'Other',
] as const;
export const COUNTRY_OPTIONS = ['Australia', 'New Zealand'] as const;
export const EMPLOYMENT_TYPE_OPTIONS = [
  'Self Employed',
  'PAYE',
  'Unemployed',
  'Retired',
] as const;
export const EMPLOYMENT_TIMING_OPTIONS = ['Current', 'Previous'] as const;
export const EMPLOYMENT_STATUS_OPTIONS = [
  'Full Time',
  'Part Time',
  'Casual',
  'Contract',
] as const;
export const EMPLOYMENT_SECTOR_OPTIONS = ['Private', 'Public'] as const;
export const ASSET_TYPE_OPTIONS = [
  'Home Contents',
  'Motor Vehicle',
  'Superannuation',
  'Real Estate',
] as const;
/** The liability types the mapper produces (Infynity offers many more). */
export const LIABILITY_TYPE_OPTIONS = ['Mortgage Loan', 'Credit Card'] as const;
export const REPAYMENT_TYPE_OPTIONS = [
  'principal_and_interest',
  'interest_only',
] as const;
export const RATE_TYPE_OPTIONS = ['fixed', 'variable'] as const;

export type Title = (typeof TITLE_OPTIONS)[number];
export type Gender = (typeof GENDER_OPTIONS)[number];
export type MaritalStatus = (typeof MARITAL_STATUS_OPTIONS)[number];
export type YesNo = (typeof YES_NO_OPTIONS)[number];
export type YesNoTitleCase = (typeof YES_NO_TITLECASE_OPTIONS)[number];
export type CurrentHousingSituation =
  (typeof HOUSING_SITUATION_OPTIONS)[number];
export type EmploymentType = (typeof EMPLOYMENT_TYPE_OPTIONS)[number];
export type EmploymentTiming = (typeof EMPLOYMENT_TIMING_OPTIONS)[number];
export type EmploymentStatus = (typeof EMPLOYMENT_STATUS_OPTIONS)[number];
export type EmploymentSector = (typeof EMPLOYMENT_SECTOR_OPTIONS)[number];
export type AssetType = (typeof ASSET_TYPE_OPTIONS)[number];
export type LiabilityType = (typeof LIABILITY_TYPE_OPTIONS)[number];
export type RepaymentType = (typeof REPAYMENT_TYPE_OPTIONS)[number];
export type RateType = (typeof RATE_TYPE_OPTIONS)[number];

// ── Payload models (mirror the Python pydantic models) ──
export interface EmploymentInformation {
  employment_type?: EmploymentType | null;
  current_or_previous?: EmploymentTiming | null;
  employment_status?: EmploymentStatus | null;
  employment_sector?: EmploymentSector | null;
  employer_name?: string | null;
  occupation_role?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  title?: Title | null;
  first_name?: string | null;
  last_name?: string | null;
  mobile_number?: string | null;
  address?: string | null;
}

/**
 * A current/previous address: the raw text plus its parsed parts. `full_address`
 * is what actually gets typed into the form's "Edit Address" modal (a single
 * Non-Standard street-address line); `street_address`, `state`, `postcode`,
 * `country` are parsed out purely for the editable review payload, not for a
 * second write. `start_date` / `end_date` are DD/MM/YYYY like `date_of_birth`;
 * `end_date` is only meaningful for a previous address — a current address has
 * none.
 */
export interface AddressDetail {
  full_address?: string | null;
  street_address?: string | null;
  building_name?: string | null;
  floor_no?: string | null;
  unit_no?: string | null;
  street_no?: string | null;
  street_name?: string | null;
  street_type?: string | null;
  suburb_city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

/**
 * A mailing / post-settlement address — copies current_address's full
 * breakdown minus the dates: unlike `AddressDetail`, nothing downstream needs
 * a start/end date for a mailing or post-settlement address.
 */
export interface MailingAddress {
  full_address?: string | null;
  street_address?: string | null;
  building_name?: string | null;
  floor_no?: string | null;
  unit_no?: string | null;
  street_no?: string | null;
  street_name?: string | null;
  street_type?: string | null;
  suburb_city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}

export interface ApplicantDetail {
  primary_applicant?: YesNo | null;
  title?: Title | null;
  first_name: string;
  surname: string;
  middle_name?: string | null;
  email?: string | null;
  marital_status?: MaritalStatus | null;
  date_of_birth?: string | null; // DD/MM/YYYY
  gender?: Gender | null;
  current_housing_situation?: CurrentHousingSituation | null;
  permanent_in_australia?: YesNoTitleCase;
  country?: string | null;
  number_of_dependents?: number | null;
  dependents_ages?: number[];
  home_phone?: string | null;
  work_phone?: string | null;
  fax_number?: string | null;
  mobile?: string | null;
  current_address?: AddressDetail | null;
  previous_address?: AddressDetail | null;
  post_settlement_address?: MailingAddress | null;
  mailing_address?: MailingAddress | null;
  employments?: EmploymentInformation[] | null;
}

/**
 * One applicant returned by the mapper: an id plus the nested `detail` object.
 * Extra keys are allowed so any unknown fields still show up.
 */
export interface GatheredApplicant {
  applicant_id: string;
  detail: ApplicantDetail;
  [key: string]: unknown;
}

export interface OwnerShare {
  name: string;
  percentage: number;
}

/** A parsed property address as carried on a Real Estate asset. */
export interface RealEstateAddress {
  full_address?: string | null;
  building_name?: string | null;
  unit_no?: string | null;
  floor_no?: string | null;
  street_no?: string | null;
  street_name?: string | null;
  street_type?: string | null;
  street_address?: string | null;
  suburb_city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

/** One rental-income line against a Real Estate asset. */
export interface RentalIncome {
  name?: string | null;
  frequency?: string | null;
  amount?: string | null;
}

/** Real-estate-specific detail nested on a `Real Estate` AssetEntry. */
export interface RealEstateDetail {
  use_as_security?: string | null;
  being_purchased?: string | null;
  being_sold?: string | null;
  purchase_date?: string | null;
  address?: RealEstateAddress | null;
  rental_income?: RentalIncome[] | null;
}

export interface AssetEntry {
  asset_type: AssetType;
  value: string; // normalized numeric string, e.g. "12500"
  description?: string | null;
  ownership?: OwnerShare[] | null;
  /** Only present for `Real Estate` assets. */
  real_estate?: RealEstateDetail | null;
}

/**
 * One Liabilities row — a mortgage loan (one per property mortgage) or a credit
 * card. Filled AFTER the assets: a Mortgage Loan's "Mortgage Loan For" dropdown
 * only lists addresses that the property's Real Estate asset already put on the
 * account.
 *
 * `description` is the identity field and carries the account's last four digits
 * ("HL - ANZ - 6452"); it's what tells two rows apart and what the fill step
 * dedupes on. Amounts are normalized numeric strings, like an asset's `value`.
 *
 * `account_number` is the FULL loan account number. Don't render it in full —
 * mask to the last four, which `description` already carries.
 */
export interface LiabilityEntry {
  liability_type: LiabilityType;
  /** Match hint resolved against the account's existing addresses at fill time. */
  mortgage_for_address?: string | null;
  clearing_from_this_loan?: string | null;
  financial_institution?: string | null;
  description?: string | null;
  account_name?: string | null;
  bsb?: string | null;
  account_number?: string | null;
  repayment_type?: RepaymentType | null;
  rate_type?: RateType | null;
  fixed_maturity_date?: string | null;
  loan_maturity_date?: string | null;
  amount_owing?: string | null;
  limit_amount?: string | null;
  monthly_repayment?: string | null;
  interest_rate?: string | null;
  ownership?: OwnerShare[] | null;
}

// ── Last-synced ("previous") state, from BrokerCopilot's own DB ──
// Written by the agent's report callback, read back here so the reviewer can diff
// the pending mapped payload against what actually last reached Infynity.
export type InfynitySyncStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'PARTIAL';
export type InfynitySyncOperation = 'CREATE' | 'UPDATE' | 'DELETE';

/** Shared last_* surface (assessment-scoped financials row). */
export interface AssessmentSyncState {
  infynity_account_id?: string | null;
  last_operation?: InfynitySyncOperation | null;
  last_status?: InfynitySyncStatus | null;
  last_succeeded_at?: string | null;
  last_failed_at?: string | null;
  last_error?: string | null;
  last_updated_data?: Record<string, unknown> | null;
}

/** One client's last-synced profile row (`last_updated_data` mirrors ApplicantDetail). */
export interface ClientSyncState extends AssessmentSyncState {
  email?: string | null;
  client_id: string;
  last_assessment_id?: string | null;
  last_updated_data?:
    | (Partial<ApplicantDetail> & Record<string, unknown>)
    | null;
}

export interface PreviousSync {
  /** Assessment-scoped snapshot (assets/financials/other). Strictly this assessment. */
  assessment: AssessmentSyncState | null;
  /** Per-applicant profile snapshots, matched to the pending applicants by email. */
  clients: { email: string; sync: ClientSyncState | null }[];
}

export interface AssessmentCreateInformation {
  assessment_id: string;
  applicants: GatheredApplicant[];
  assets: AssetEntry[];
  /**
   * Mortgage loans and credit cards, mapped from the assessment's calculation
   * sections (not HubSpot). Filled after the assets, because a Mortgage Loan can
   * only be linked to a property the Real Estate asset already created.
   */
  liabilities: LiabilityEntry[];
  skipped_applicant_ids: string[];
  /**
   * ISO timestamp of the last successful sync for this assessment (null if it
   * has never succeeded). Lets the reviewer see it was already updated without
   * having to re-run the update to find out. Output-only: set by
   * create-information and ignored on submit.
   */
  last_succeeded_at?: string | null;
  /** Completion time of the most recent FAILED job; null if the latest didn't fail. */
  last_failed_at?: string | null;
  /** Failure message of the most recent failed job. Output-only. */
  last_error?: string | null;
  /**
   * What the most recent job resolved to — `create` (a new record was made) or
   * `update` (it matched an existing client). Output-only.
   */
  last_operation?: 'create' | 'update' | null;
  /**
   * The last-synced snapshot from BrokerCopilot's DB (agent-reported), used to
   * diff against the pending payload above. `null` when never synced or if the
   * lookup failed (the review still renders — the diff just isn't shown).
   */
  previous_sync?: PreviousSync | null;
}

export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface AssessmentJob {
  job_id: string;
  assessment_id: string;
  status: JobStatus;
  phase: string;
  attempt: number;
  create_information?: AssessmentCreateInformation | null;
  applicants: Record<string, unknown>[];
  financials: Record<string, unknown>[];
  result?: Record<string, unknown> | null;
  error?: string | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

/**
 * The immediate receipt from any of the queueing routes. Deliberately NOT the
 * job record: nothing about Infynity is known yet when this returns.
 *
 * `deduplicated` is true only when an existing queued/running job was returned
 * — false for a new job and for a completed job queued for another attempt.
 */
export interface JobSubmission {
  received: true;
  job_id: string;
  deduplicated: boolean;
}

export type ServiceStatus = 'starting' | 'ok' | 'degraded';

export interface ServiceHealth {
  status: ServiceStatus;
  browser_started: boolean;
  infynity_logged_out?: boolean | null;
  queued_jobs: number;
  detail?: string | null;
}

export interface ClientAccount {
  account_id: string;
  name: string;
  mobile?: string | null;
  email?: string | null;
  broker?: string | null;
  date_created?: string | null;
  date_accessed?: string | null;
}

export interface AccountSearchRequest {
  first_name: string;
  last_name: string;
  email: string;
}

export interface AccountSearchResponse {
  exists: boolean;
  searched_names: string[];
  matched?: ClientAccount | null;
  matched_applicant_name?: string | null;
  matched_applicant_email?: string | null;
  match_source?: 'table_row' | 'account_applicant' | null;
  results: ClientAccount[];
}

export const infynityCreationApi = createApi({
  reducerPath: 'infynityCreationApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    // Model this read as a mutation because it is an explicit, always-fresh
    // user action. RTK Query therefore does not cache or reuse its result.
    getInfynityCreateInformation: builder.mutation<
      AssessmentCreateInformation,
      string
    >({
      query: (assessmentId) => ({
        url: `v1/infynity-agent/create-information/${assessmentId}`,
        method: 'GET',
      }),
      transformResponse: (r: ApiResponse<AssessmentCreateInformation>) =>
        r.data,
    }),
    /**
     * CLIENT DETAILS half: applicant form, addresses, employment. Creates the
     * client if it doesn't exist yet. Does not touch the Financials tab.
     */
    updateInfynityAccountFromAssessment: builder.mutation<
      JobSubmission,
      string
    >({
      query: (assessmentId) => ({
        url: `v1/infynity-agent/update-from-assessment/${assessmentId}`,
        method: 'POST',
      }),
      transformResponse: (r: ApiResponse<JobSubmission>) => r.data,
    }),
    /**
     * FINANCIALS half: assets, then liabilities. Never creates a client, so the
     * client-details operation has to have run at least once first.
     */
    updateInfynityFinancialsFromAssessment: builder.mutation<
      JobSubmission,
      string
    >({
      query: (assessmentId) => ({
        url: `v1/infynity-agent/update-financials-from-assessment/${assessmentId}`,
        method: 'POST',
      }),
      transformResponse: (r: ApiResponse<JobSubmission>) => r.data,
    }),
    getInfynityJob: builder.query<AssessmentJob, string>({
      query: (jobId) => `v1/infynity-agent/jobs/${jobId}`,
      transformResponse: (r: ApiResponse<AssessmentJob>) => r.data,
    }),
    getInfynityFinancialsJob: builder.query<AssessmentJob, string>({
      query: (jobId) => `v1/infynity-agent/financials-jobs/${jobId}`,
      transformResponse: (r: ApiResponse<AssessmentJob>) => r.data,
    }),
    getInfynityJobByAssessment: builder.query<AssessmentJob, string>({
      query: (assessmentId) =>
        `v1/infynity-agent/assessments/${assessmentId}/job`,
      transformResponse: (r: ApiResponse<AssessmentJob>) => r.data,
    }),
    searchInfynityAccount: builder.mutation<
      AccountSearchResponse,
      AccountSearchRequest
    >({
      query: (body) => ({
        url: 'v1/infynity-agent/search',
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<AccountSearchResponse>) => r.data,
    }),
    getInfynityReadiness: builder.query<ServiceHealth, void>({
      query: () => 'v1/infynity-agent/health/ready',
      transformResponse: (r: ApiResponse<ServiceHealth>) => r.data,
    }),
  }),
});

export const {
  useGetInfynityCreateInformationMutation,
  useUpdateInfynityAccountFromAssessmentMutation,
  useUpdateInfynityFinancialsFromAssessmentMutation,
  useGetInfynityJobQuery,
  useGetInfynityFinancialsJobQuery,
  useGetInfynityJobByAssessmentQuery,
  useSearchInfynityAccountMutation,
  useGetInfynityReadinessQuery,
} = infynityCreationApi;
